// Which 3D model belongs to which branch, company and landing page — one typed table (02/10/2569).
//
// `assets.ts` answers "where is the file and whose is it" (the model table and its rights). This file answers
// "what is it called, what kind of thing is it, which branch and company is it, and which landing page
// may use it" so a page or a person picks the right one by name instead of by file name.
//
// Rules the table keeps (owner, 02/10/2569):
// - only things that exist are listed; a branch with no model of its own simply has none here, and its
//   landing page uses that branch's own photos, video or signage instead — never another branch's model;
// - every row is tied to the branch and company it really belongs to (company codes are
//   master.entities.entity_code, platform/docs/NAMING.md); a row whose owner is not confirmed says so by
//   `ownership: 'unconfirmed'` and carries no branch and no landing;
// - nothing in here is written for a public page to print: names are labels for people picking an asset
//   in the hub, `usedBy` is the code that references the model (the test greps each file).
import type { ModelName } from './assets';

/** master.entities.entity_code (platform/docs/NAMING.md, "บริษัท"). */
export type EntityCode =
  'SPMS' | 'EST' | 'CPK' | 'PK' | 'SP_BAN_CAR_CARE' | 'TNK' | 'AA' | 'PREEDA_NEXT';

/** The public sites that have a landing page of their own. */
export type LandingSite =
  | 'preedanext.com'
  | 'preedagroup.com'
  | 'spms-est.com'
  | 'pkproklasse.com'
  | 'sp-bancarcare.com'
  | 'cpksalesandmarketing.com'
  | 'tanakritkhajornkovit.com';

export interface Landing {
  site: LandingSite;
  /** folder in this repository */
  app: string;
  companies: readonly EntityCode[];
  /** Existing homepage frame in the owner's shared Figma file. */
  designNode: string;
  /** the owner's own site: it may show his design work for a company, so company and landing need not match */
  personal?: true;
}

export const LANDINGS: Readonly<Record<LandingSite, Landing>> = {
  'preedanext.com': { site: 'preedanext.com', app: 'apps/preedanext', companies: ['PREEDA_NEXT'], designNode: '1315:77025' },
  // master.entities has no separate Preeda Group row: the group umbrella row is PREEDA_NEXT (entity_kind group_umbrella)
  'preedagroup.com': {
    site: 'preedagroup.com',
    app: 'apps/preedagroup',
    companies: ['PREEDA_NEXT'],
    designNode: '1315:77431',
  },
  'spms-est.com': { site: 'spms-est.com', app: 'apps/spms-est', companies: ['SPMS', 'EST'], designNode: '1315:77786' },
  'pkproklasse.com': { site: 'pkproklasse.com', app: 'apps/pkproklasse', companies: ['PK'], designNode: '1315:78261' },
  'sp-bancarcare.com': {
    site: 'sp-bancarcare.com',
    app: 'apps/sp-bancarcare',
    companies: ['SP_BAN_CAR_CARE'],
    designNode: '1315:78617',
  },
  'cpksalesandmarketing.com': {
    site: 'cpksalesandmarketing.com',
    app: 'apps/preedagroup',
    companies: ['CPK'],
    designNode: '579:821',
  },
  'tanakritkhajornkovit.com': {
    site: 'tanakritkhajornkovit.com',
    app: 'apps/tanakritkhajornkovit',
    companies: ['TNK'],
    designNode: '1315:78929',
    personal: true,
  },
};

export function landingDesignUrl(landing: Landing): string {
  return `https://www.figma.com/design/Vlfim8sphO761cgu6yncSh?node-id=${landing.designNode.replace(':', '-')}`;
}

export type BranchId =
  | 'preeda-group'
  | 'preeda-next'
  | 'experience-center'
  | 'spms-est-head-office'
  | 'pk-liab-duan'
  | 'pk-pro-lab'
  | 'sp-ban-carcare'
  | 'pk-office';

export interface Branch {
  id: BranchId;
  /** what people call it */
  name: string;
  nameTh?: string;
  /** a physical place, the group itself, or a brand */
  kind: 'place' | 'group' | 'brand';
  companies: readonly EntityCode[];
  landings: readonly LandingSite[];
}

export const BRANCHES: readonly Branch[] = [
  {
    id: 'preeda-group',
    name: 'Preeda Group',
    kind: 'group',
    companies: ['PREEDA_NEXT'],
    landings: ['preedagroup.com'],
  },
  {
    id: 'preeda-next',
    name: 'PREEDA NEXT',
    kind: 'brand',
    companies: ['PREEDA_NEXT'],
    landings: ['preedanext.com'],
  },
  {
    id: 'experience-center',
    name: 'PREEDA NEXT Experience Center',
    kind: 'place',
    companies: ['PREEDA_NEXT'],
    landings: ['preedanext.com'],
  },
  {
    // the registered address of SPMS, EST, CPK and PK Pro Work (SITES.md); the PK Zone is on the same compound
    id: 'spms-est-head-office',
    name: 'SPMS · EST head office, Bang Khae',
    nameTh: 'สำนักงานใหญ่',
    kind: 'place',
    companies: ['SPMS', 'EST', 'CPK', 'PK'],
    landings: ['spms-est.com'],
  },
  {
    id: 'pk-liab-duan',
    name: 'PK Pro Klasse, Liab Duan branch',
    nameTh: 'สาขาเลียบด่วน',
    kind: 'place',
    companies: ['PK'],
    landings: ['pkproklasse.com'],
  },
  {
    id: 'pk-pro-lab',
    name: 'PK Pro Lab',
    nameTh: 'PK Pro Lab สาขา 002',
    kind: 'place',
    companies: ['PK'],
    landings: ['pkproklasse.com'],
  },
  {
    id: 'sp-ban-carcare',
    name: 'SP Ban Carcare, Nakhon Ratchasima',
    kind: 'brand',
    companies: ['SP_BAN_CAR_CARE'],
    landings: ['sp-bancarcare.com'],
  },
  {
    id: 'pk-office',
    name: 'PK Pro Klasse office, four floors',
    kind: 'place',
    companies: ['PK'],
    landings: ['tanakritkhajornkovit.com'],
  },
];

/** What the thing is. A building is a whole site; an office is a building shown for its rooms. */
export type SiteAssetKind =
  'building' | 'office' | 'signage' | 'signage-kit' | 'mark-3d' | 'car' | 'terrain';

export const SITE_ASSET_KIND_LABEL: Readonly<Record<SiteAssetKind, string>> = {
  building: 'Building',
  office: 'Office',
  signage: 'Signage',
  'signage-kit': 'Signage kit',
  'mark-3d': '3D mark',
  car: 'Car',
  terrain: 'Terrain',
};

export interface SiteAsset {
  /** the S-number in platform/docs/OFFICES-AND-STUDIOS.md; it never changes and is never reused */
  number: string;
  /** the model's name in assets.ts */
  model: ModelName;
  /** the name people call it by */
  name: string;
  nameTh?: string;
  kind: SiteAssetKind;
  /** one line: what is drawn, and from what */
  what: string;
  /** `unconfirmed`: the file is ours but which company it belongs to is not established; `multi-brand`: one file holds several companies' variants */
  ownership: 'confirmed' | 'unconfirmed' | 'multi-brand';
  branch: BranchId | null;
  companies: readonly EntityCode[];
  /** landing pages this asset belongs on */
  landings: readonly LandingSite[];
  /** landing pages where this is the right scene for the full-screen first section */
  heroFor: readonly LandingSite[];
  /** repository files that reference the model today (checked by the test) */
  usedBy: readonly string[];
  made: '2569-09-28' | '2569-10-02' | 'earlier';
}

export const SITE_ASSETS: readonly SiteAsset[] = [
  {
    number: 'S01',
    model: 'spmsEstHeadOffice',
    name: 'SPMS · EST head office',
    nameTh: 'สำนักงานใหญ่',
    kind: 'building',
    what: 'Compound with Building A, Building B, the PK Zone hall, gates and signage, from the 2556 permit set and the PK Zone survey',
    ownership: 'confirmed',
    branch: 'spms-est-head-office',
    companies: ['SPMS', 'EST', 'CPK', 'PK'],
    landings: ['spms-est.com'],
    heroFor: ['spms-est.com'],
    usedBy: ['apps/spms-est/src/components/HeadOfficeSection.tsx'],
    made: '2569-09-28',
  },
  {
    number: 'S02',
    model: 'pnxExperienceCenter',
    name: 'PREEDA NEXT Experience Center',
    kind: 'building',
    what: "Four-floor concept building with plots, roads, annex and PREEDA NEXT signage, from the architect's concept model",
    ownership: 'confirmed',
    branch: 'experience-center',
    companies: ['PREEDA_NEXT'],
    landings: ['preedanext.com'],
    heroFor: [],
    // /company shows the owner's Cycles stills of this building since 06/10/2569 (packages/ui/src/owner-renders.tsx)
    usedBy: [],
    made: '2569-09-28',
  },
  {
    number: 'S03',
    model: 'pkLiabDuan',
    name: 'PK Pro Klasse Liab Duan branch',
    nameTh: 'สาขาเลียบด่วน',
    kind: 'building',
    what: 'Branch building with install bays, street glass pavilion and red-fin facade, from the 3M CAR construction drawings',
    ownership: 'confirmed',
    branch: 'pk-liab-duan',
    companies: ['PK'],
    landings: ['pkproklasse.com'],
    heroFor: ['pkproklasse.com'],
    // the home page left the 3D scene 06/10/2569; it stays offered (heroFor) for the later HeroStage enhancement
    // /contact shows the owner's Cycles stills of the branch since 06/10/2569 (packages/ui/src/owner-renders.tsx)
    usedBy: [],
    made: '2569-09-28',
  },
  {
    number: 'S04',
    model: 'spBaanCarcareSignage',
    name: 'SP Ban Carcare signage',
    nameTh: 'ป้ายร้าน',
    kind: 'signage',
    what: 'Sign variants for the brand: fascia, blade, pylon, lightbox, flag, banners',
    ownership: 'confirmed',
    branch: 'sp-ban-carcare',
    companies: ['SP_BAN_CAR_CARE'],
    landings: ['sp-bancarcare.com'],
    heroFor: ['sp-bancarcare.com'],
    // the home page left the 3D scene for a video hero (f09315081); the scene manifest `sp-bancarcare.home` still names it
    usedBy: ['packages/scene-registry/src/manifests.ts'],
    made: '2569-09-28',
  },
  {
    number: 'S05',
    model: 'pkOfficeSourceWeb',
    name: 'PK Pro Klasse office, four floors',
    kind: 'office',
    what: 'The owner-designed four-floor office, web copy without materials',
    ownership: 'confirmed',
    branch: 'pk-office',
    companies: ['PK'],
    landings: ['tanakritkhajornkovit.com'],
    heroFor: [],
    usedBy: [],
    made: 'earlier',
  },
  {
    number: 'S06',
    model: 'pkOfficeSourceWebMaterials',
    name: 'PK Pro Klasse office, four floors, with materials',
    kind: 'office',
    what: "The owner-designed four-floor office, web copy with the architect's materials",
    ownership: 'confirmed',
    branch: 'pk-office',
    companies: ['PK'],
    landings: ['tanakritkhajornkovit.com'],
    heroFor: [],
    usedBy: [],
    made: 'earlier',
  },
  {
    number: 'S07',
    model: 'preedaNextLogo',
    name: 'PREEDA NEXT logo',
    kind: 'mark-3d',
    what: 'The PREEDA NEXT symbol as a 3D object',
    ownership: 'confirmed',
    branch: 'preeda-next',
    companies: ['PREEDA_NEXT'],
    landings: ['preedanext.com'],
    heroFor: [],
    usedBy: [],
    made: 'earlier',
  },
  {
    number: 'S32',
    model: 'pkProlab',
    name: 'PK Pro Lab',
    nameTh: 'PK Pro Lab สาขา 002',
    kind: 'building',
    what: "Shell and rooms from the 13-sheet fit-out drawing set: the owner's PK Pro Lab (branch 002). The file was first named after another company's building (FAB) by mistake; PK Pro Lab signage is added to the model",
    ownership: 'confirmed',
    branch: 'pk-pro-lab',
    companies: ['PK'],
    landings: ['pkproklasse.com'],
    heroFor: [],
    usedBy: [],
    made: '2569-09-28',
  },
  {
    number: 'S33',
    model: 'signageKit',
    name: 'Signage kit, all brands',
    kind: 'signage-kit',
    what: 'Every sign variant for PREEDA NEXT, PK, SP, SPMS EST and CPK in one file, for picking a design — not for one landing page',
    ownership: 'multi-brand',
    branch: null,
    companies: ['PREEDA_NEXT', 'PK', 'SP_BAN_CAR_CARE', 'SPMS', 'EST', 'CPK'],
    landings: [],
    heroFor: [],
    usedBy: [],
    made: '2569-09-28',
  },
  {
    number: 'S34',
    model: 'mark3dEst',
    name: 'EST 3D mark',
    kind: 'mark-3d',
    what: 'EST (2014) Co., Ltd. artwork extruded unchanged',
    ownership: 'confirmed',
    branch: 'spms-est-head-office',
    companies: ['EST'],
    landings: ['spms-est.com'],
    heroFor: [],
    usedBy: [],
    made: '2569-10-02',
  },
  {
    number: 'S35',
    model: 'mark3dPreedaGroup',
    name: 'Preeda Group 3D mark',
    kind: 'mark-3d',
    what: 'The group symbol on its tile, extruded unchanged',
    ownership: 'confirmed',
    branch: 'preeda-group',
    companies: ['PREEDA_NEXT'],
    landings: ['preedagroup.com'],
    heroFor: ['preedagroup.com'],
    // the home page's first screen (06/10/2569), through the scene manifest `preedagroup.home`
    usedBy: ['packages/scene-registry/src/manifests.ts'],
    made: '2569-10-02',
  },
  {
    number: 'S36',
    model: 'mark3dPkProKlasse',
    name: 'PK Pro Klasse 3D mark',
    kind: 'mark-3d',
    what: 'PK Pro Klasse artwork extruded unchanged',
    ownership: 'confirmed',
    branch: 'pk-liab-duan',
    companies: ['PK'],
    landings: ['pkproklasse.com'],
    heroFor: [],
    usedBy: [],
    made: '2569-10-02',
  },
  {
    number: 'S37',
    model: 'mark3dSpBanCarcare',
    name: 'SP Ban Carcare 3D mark',
    kind: 'mark-3d',
    what: 'SP Ban Carcare artwork extruded unchanged',
    ownership: 'confirmed',
    branch: 'sp-ban-carcare',
    companies: ['SP_BAN_CAR_CARE'],
    landings: ['sp-bancarcare.com'],
    heroFor: [],
    usedBy: [],
    made: '2569-10-02',
  },
];

export function assetsForLanding(site: LandingSite): readonly SiteAsset[] {
  return SITE_ASSETS.filter((asset) => asset.landings.includes(site));
}

export function assetsForBranch(id: BranchId): readonly SiteAsset[] {
  return SITE_ASSETS.filter((asset) => asset.branch === id);
}

/** The assets that fit the full-screen first section of a landing page; empty when the landing has none. */
export function heroAssetsFor(site: LandingSite): readonly SiteAsset[] {
  return SITE_ASSETS.filter((asset) => asset.heroFor.includes(site));
}

/** How many 3D scenes a landing page can draw on (its own assets only). */
export function sceneCount(site: LandingSite): number {
  return assetsForLanding(site).length;
}
